/**
 * Profile API Service
 * Handles all API calls related to user profile management
 * Database Tables: users, user_profiles, languages, language_user, payment_transactions
 */

import axios from 'axios';
import Cookie, { JWT_COOKIE_NAME } from '../cookie';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ? `${process.env.REACT_APP_API_BASE_URL}/api` : 'http://localhost:5000/api';

/**
 * Create axios instance with default config
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Add auth token to requests
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = Cookie.get(JWT_COOKIE_NAME);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Handle response errors
 */
apiClient.interceptors.response.use(
  (response) => {
    console.log('📥 API Response Interceptor - SUCCESS:');
    console.log('- Status:', response.status);
    console.log('- URL:', response.config.url);
    console.log('- Method:', response.config.method);
    console.log('- Response data:', response.data);
    return response;
  },
  (error) => {
    console.error('📥 API Response Interceptor - ERROR:');
    console.error('- Status:', error.response?.status);
    console.error('- URL:', error.config?.url);
    console.error('- Method:', error.config?.method);
    console.error('- Error data:', error.response?.data);
    console.error('- Error message:', error.message);
    
    if (error.response?.status === 401) {
      console.error('🚨 Unauthorized - redirecting to login');
      // Unauthorized - redirect to login
      localStorage.removeItem('authToken');
      Cookie.remove(JWT_COOKIE_NAME);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * GET /api/user/profile
 * Fetch user profile data from users and user_profiles tables
 * @returns {Promise<Object>} User data: {id, name, email, email_verified_at, username, avatar_url, created_at, updated_at}
 */
export const fetchUserProfile = async () => {
  try {
    const response = await apiClient.get('/user/profile');
    return response.data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error.response?.data || error.message;
  }
};

/**
 * PUT /api/user/profile
 * Update user profile data (name and email in users table, username and language_preference in user_profiles table)
 * @param {Object} data - Updated profile data {name, username, email, languagePreference, avatar}
 * @returns {Promise<Object>} Updated user data
 */
export const updateUserProfile = async (data) => {
  try {
    console.log('profileAPI.updateUserProfile called with:', data);
    console.log('API Base URL:', API_BASE_URL);
    console.log('Full endpoint:', `${API_BASE_URL}/user/profile`);
    
    console.log('About to send PUT request...');
    
    let requestData;
    let config = {};
    
    // Check if we have an avatar file to upload
    if (data.avatar && data.avatar instanceof File) {
      console.log('Avatar file detected, using FormData');
      // Use FormData for file upload
      requestData = new FormData();
      requestData.append('name', data.name || '');
      requestData.append('username', data.username || '');
      requestData.append('email', data.email || '');
      requestData.append('languagePreference', data.languagePreference || '');
      requestData.append('avatar', data.avatar);
      
      // Set content type to multipart/form-data
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    } else {
      console.log('No avatar file, using JSON');
      // Use regular JSON
      requestData = {
        name: data.name,
        username: data.username,
        email: data.email,
        languagePreference: data.languagePreference,
      };
    }
    
    const response = await apiClient.put('/user/profile', requestData, config);
    
    console.log('PUT request completed');
    console.log('profileAPI.updateUserProfile response status:', response.status);
    console.log('profileAPI.updateUserProfile response data:', response.data);
    
    // Check if the response is successful
    if (response.status >= 200 && response.status < 300) {
      console.log('✅ API call successful, returning data');
      return response.data;
    } else {
      console.log('❌ API call returned non-success status:', response.status);
      throw new Error(`HTTP ${response.status}: ${response.data?.error || 'Unknown error'}`);
    }
    
  } catch (error) {
    console.error('❌ profileAPI.updateUserProfile error caught:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Error message:', error.message);
    
    // Extract error message from different possible formats
    let errorMessage = 'Failed to update profile';
    
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    console.error('Throwing error with message:', errorMessage);
    
    // Create error object that includes the original error details
    const apiError = new Error(errorMessage);
    apiError.status = error.response?.status;
    apiError.response = error.response;
    throw apiError;
  }
};

/**
 * POST /api/user/change-password
 * Change user password
 * @param {Object} data - Password change data {current_password, new_password}
 * @returns {Promise<Object>} Success response
 */
export const changePassword = async (data) => {
  try {
    const response = await apiClient.post('/user/change-password', {
      current_password: data.currentPassword,
      new_password: data.newPassword,
    });
    
    return response.data;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error.response?.data || error.message;
  }
};

/**
 * GET /api/user/subscriptions
 * Fetch user subscriptions from language_user table
 * Includes: language name, subscription date, type (Monthly/Annual), expiration date
 * @returns {Promise<Object>} {success, subscriptions, count}
 */
export const fetchUserSubscriptions = async () => {
  try {
    const response = await apiClient.get('/user/subscriptions');
    return response.data;
  } catch (error) {
    console.error('Error fetching subscriptions:', error);
    throw error.response?.data || error.message;
  }
};

/**
 * GET /api/languages
 * Fetch all available languages from languages table
 * @returns {Promise<Array>} Array of language objects {id, name, description}
 */
export const fetchLanguages = async () => {
  try {
    const response = await apiClient.get('/languages');
    return response.data;
  } catch (error) {
    console.error('Error fetching languages:', error);
    throw error.response?.data || error.message;
  }
};

/**
 * DELETE /api/users/account
 * Delete user account and all associated data
 * Requires password confirmation for security
 * @param {Object} data - Deletion confirmation {password}
 * @returns {Promise<Object>} Success response
 */
export const deleteUserAccount = async (data) => {
  try {
    const response = await apiClient.delete('/users/account', {
      data: {
        password: data.password,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error.response?.data || error.message;
  }
};

/**
 * Upload profile image
 * Utility function to upload and compress image before API call
 * @param {File} file - Image file (JPG, PNG, WebP, max 5MB)
 * @returns {Promise<File>} Compressed image file
 */
export const compressImage = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions (max 800x800)
        const maxSize = 800;
        if (width > height) {
          if (width > maxSize) {
            height *= maxSize / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width *= maxSize / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, { type: file.type });
            resolve(compressedFile);
          },
          file.type,
          0.8 // Quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = event.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * GET /api/user/check-email
 * Check if email is available (not already in use)
 * @param {string} email - Email to check
 * @returns {Promise<Object>} {available: boolean}
 */
export const checkEmailAvailability = async (email) => {
  try {
    const response = await apiClient.get('/user/check-email', {
      params: { email }
    });
    return response.data;
  } catch (error) {
    console.error('Error checking email availability:', error);
    throw error.response?.data || error.message;
  }
};

export default apiClient;
